import Swal from "sweetalert2";

/**
 * Extracts the total marks / max score from a userPrompt string.
 * Supports patterns such as:
 * - "Total Marks: 20"
 * - "**Total Marks:** 20"
 * - "Total Marks: 100"
 * - "Maximum Marks: 50"
 * - "Max Marks: 75"
 *
 * @param {string} prompt
 * @returns {number|null}
 */
export const extractUserPromptMaxScore = (prompt) => {
  if (!prompt || typeof prompt !== "string") return null;

  // 1. Check line-by-line first for highest precision (ignoring section summary lines)
  const lines = prompt.split(/\r?\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Pattern matching beginning of line:
    // e.g., "Total Marks: 20", "# Total Marks: 20", "**Total Marks:** 20", "Max Marks: 50"
    const lineMatch = line.match(
      /^(?:#+\s*)?(?:\*\*|__)?(?:total\s*(?:maximum\s*)?|max(?:imum)?\s*)marks?(?:\*\*|__)?\s*[:=-]?\s*(?:\*\*|__)?\s*([0-9]+(?:\.[0-9]+)?)/i
    );
    if (lineMatch && lineMatch[1]) {
      const parsed = parseFloat(lineMatch[1]);
      if (!isNaN(parsed)) return parsed;
    }
  }

  // 2. Fallback search anywhere in the text (ensuring 'total' or 'max' precedes 'marks')
  const generalMatch = prompt.match(
    /(?:total\s*(?:maximum\s*)?|max(?:imum)?\s*)marks?\s*[*_]*\s*[:=-]?\s*[*_]*\s*([0-9]+(?:\.[0-9]+)?)/i
  );
  if (generalMatch && generalMatch[1]) {
    const parsed = parseFloat(generalMatch[1]);
    if (!isNaN(parsed)) return parsed;
  }

  return null;
};

/**
 * Compares extracted userPrompt max score with test max score.
 *
 * @param {string} prompt
 * @param {number|string} testMaxScore
 * @returns {{ isScoreMatch: boolean, userPromptMaxScore: number|null, testMaxScore: number|null }}
 */
export const checkUserPromptScoreMatch = (prompt, testMaxScore) => {
  const userPromptMaxScore = extractUserPromptMaxScore(prompt);
  const parsedTestMaxScore =
    testMaxScore != null && testMaxScore !== "" ? parseFloat(testMaxScore) : null;

  const validTestScore = parsedTestMaxScore !== null && !isNaN(parsedTestMaxScore);
  const validPromptScore = userPromptMaxScore !== null && !isNaN(userPromptMaxScore);

  const isScoreMatch =
    validTestScore &&
    validPromptScore &&
    (userPromptMaxScore === parsedTestMaxScore ||
      Math.abs(userPromptMaxScore - parsedTestMaxScore) < 0.001);

  return {
    isScoreMatch,
    userPromptMaxScore: validPromptScore ? userPromptMaxScore : null,
    testMaxScore: validTestScore ? parsedTestMaxScore : null,
  };
};

/**
 * Shows an easy-to-understand popup explaining to the teacher that total marks differ,
 * designed specially so older and non-technical teachers can understand immediately.
 * Offers two choices:
 *  1. Edit Max Score (if the exam total marks entered was incorrect)
 *  2. Edit Special Grading Instructions (if the question paper breakdown needs adjustment)
 *
 * @param {object} params
 * @param {number|string|null} params.testMaxScore
 * @param {number|string|null} params.promptMaxScore
 * @param {function} [params.onEditMaxScore]
 * @param {function} [params.onEditInstructions]
 */
// Clean inline SVGs that never fail to load or get overwritten by webfont issues
const ALERT_TRIANGLE_SVG = `
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f09a21" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:auto;">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
`;

const INFO_ICON_SVG = `
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#b45309" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-2px;flex-shrink:0;">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
`;

const PENCIL_SVG = `
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-2px;margin-right:6px;flex-shrink:0;">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
`;

const LIST_CHECK_SVG = `
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-2px;margin-right:6px;flex-shrink:0;">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
`;

const CHECK_CIRCLE_SVG = `
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-2px;margin-right:6px;flex-shrink:0;">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
`;

/**
 * Displays a friendly, accessible alert explaining the score mismatch to the teacher.
 * Provides two clear options:
 *  1. Change Test Total (quick update to test max score)
 *  2. Review Questions & Section Marks (modify questions / grading prompt)
 *
 * @param {object} params
 * @param {number|string|null} params.testMaxScore
 * @param {number|string|null} params.promptMaxScore
 * @param {function} [params.onEditMaxScore]
 * @param {function} [params.onEditInstructions]
 */
export const showScoreMismatchAlert = ({
  testMaxScore,
  promptMaxScore,
  onEditMaxScore,
  onEditInstructions,
}) => {
  const enteredScoreText = testMaxScore != null ? `${testMaxScore} Marks` : "Not specified";
  const detectedScoreText = promptMaxScore != null ? `${promptMaxScore} Marks` : "Could not be detected";

  const changeScoreBtnText = promptMaxScore != null
    ? `${PENCIL_SVG} Change Test Total to ${promptMaxScore} Marks`
    : `${PENCIL_SVG} Change Test Total Marks`;

  return Swal.fire({
    html: `
      <div style="font-family: 'Montserrat', sans-serif; text-align: center; padding: 4px 0;">
        <div style="width: 58px; height: 58px; margin: 0 auto 16px auto; border-radius: 50%; background: linear-gradient(135deg, #fffbeb, #fef3c7); border: 2px solid #fde68a; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 14px rgba(240, 154, 33, 0.22);">
          ${ALERT_TRIANGLE_SVG}
        </div>

        <h3 style="font-family: 'Montserrat', sans-serif; font-size: 1.25rem; font-weight: 700; color: #0f172a; margin: 0 0 14px 0; line-height: 1.35;">
          Total Marks Do Not Match
        </h3>

        <div style="background: #fdfcf9; border: 1px solid #fed7aa; border-left: 4px solid #f09a21; border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; text-align: left;">
          <div style="display: flex; align-items: center; gap: 6px; font-size: 0.78rem; font-weight: 700; color: #b45309; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">
            ${INFO_ICON_SVG} Please Review
          </div>
          <div style="font-family: 'Montserrat', sans-serif; font-size: 0.88rem; color: #1e293b; line-height: 1.55;">
            You entered <strong>${enteredScoreText}</strong> for this test, but the questions in your uploaded question paper add up to <strong>${detectedScoreText}</strong>.
          </div>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px;">
          <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 10px; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.03);">
            <div style="font-size: 0.72rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">
              Total You Entered
            </div>
            <div style="font-size: 1.2rem; font-weight: 700; color: #0f172a;">
              ${enteredScoreText}
            </div>
            <div style="font-size: 0.72rem; color: #94a3b8; margin-top: 2px;">
              (Set in test details)
            </div>
          </div>
          <div style="background: linear-gradient(135deg, #fffbeb, #fef9c3); border: 1px solid #fde68a; border-radius: 12px; padding: 12px 10px; text-align: center; box-shadow: 0 1px 3px rgba(240, 154, 33, 0.1);">
            <div style="font-size: 0.72rem; color: #b45309; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">
              In Question Paper
            </div>
            <div style="font-size: 1.2rem; font-weight: 700; color: #f09a21;">
              ${detectedScoreText}
            </div>
            <div style="font-size: 0.72rem; color: #b45309; margin-top: 2px;">
              (Found in questions)
            </div>
          </div>
        </div>

        <div style="text-align: left; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; margin-bottom: 6px; font-size: 0.82rem; color: #475569; line-height: 1.55;">
          <div style="font-weight: 600; color: #0f172a; margin-bottom: 6px;">How would you like to fix this?</div>
          <div style="margin-bottom: 4px;">
            • If <strong>${detectedScoreText}</strong> is correct: Click the orange button below to update the test total.
          </div>
          <div>
            • If <strong>${enteredScoreText}</strong> is correct: Click the black button below to check questions or section marks.
          </div>
        </div>
      </div>
    `,
    showDenyButton: true,
    showCancelButton: false,
    denyButtonText: changeScoreBtnText,
    confirmButtonText: `${LIST_CHECK_SVG} Check AI Grading Instructions`,
    confirmButtonColor: "#111111",
    denyButtonColor: "#f09a21",
    focusConfirm: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      const cancelBtn = Swal.getCancelButton();
      if (cancelBtn) {
        cancelBtn.remove();
      }
    },
    customClass: {
      popup: "score-mismatch-popup",
      actions: "score-mismatch-actions",
      confirmButton: "score-mismatch-btn-instructions",
      denyButton: "score-mismatch-btn-maxscore",
    },
  }).then((result) => {
    if (result.isDenied) {
      if (onEditMaxScore) onEditMaxScore();
    } else if (result.isConfirmed) {
      if (onEditInstructions) onEditInstructions();
    }
  });
};

/**
 * Prompts the teacher to edit the test max score with a clean input modal.
 *
 * @param {object} params
 * @param {number|string|null} params.currentMaxScore
 * @param {number|string|null} params.detectedMaxScore
 * @param {function} params.onSave
 * @param {function} [params.onCancel]
 */
export const promptEditMaxScore = async ({
  currentMaxScore,
  detectedMaxScore,
  onSave,
  onCancel,
}) => {
  const defaultVal = detectedMaxScore != null ? detectedMaxScore : currentMaxScore || "";

  const { value: newScore } = await Swal.fire({
    html: `
      <div style="font-family: 'Montserrat', sans-serif; text-align: center; padding: 4px 0;">
        <div style="width: 58px; height: 58px; margin: 0 auto 16px auto; border-radius: 50%; background: linear-gradient(135deg, #fffbeb, #fef3c7); border: 2px solid #fde68a; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 14px rgba(240, 154, 33, 0.22);">
          ${PENCIL_SVG}
        </div>

        <h3 style="font-family: 'Montserrat', sans-serif; font-size: 1.25rem; font-weight: 700; color: #0f172a; margin: 0 0 14px 0; line-height: 1.35;">
          Change Test Total Marks
        </h3>

        <div style="background: #fdfcf9; border: 1px solid #fed7aa; border-left: 4px solid #f09a21; border-radius: 12px; padding: 12px 14px; margin-bottom: 16px; font-family: 'Montserrat', sans-serif; font-size: 0.85rem; text-align: left; line-height: 1.55;">
          <div style="color: #475569; margin-bottom: 4px;">Currently set to: <strong style="color: #0f172a;">${currentMaxScore != null ? currentMaxScore : "N/A"} Marks</strong></div>
          ${detectedMaxScore != null ? `<div style="color: #b45309;">Marks detected in question paper: <strong style="color: #f09a21;">${detectedMaxScore} Marks</strong></div>` : ""}
        </div>

        <label style="display: block; font-family: 'Montserrat', sans-serif; font-weight: 600; font-size: 0.85rem; color: #334155; margin-bottom: 6px;">
          Enter the correct total marks for this test:
        </label>
      </div>
    `,
    input: "number",
    inputValue: defaultVal,
    inputAttributes: {
      min: "1",
      step: "any",
      placeholder: "e.g. 100",
    },
    showCancelButton: true,
    confirmButtonText: `${CHECK_CIRCLE_SVG} Save Total Marks`,
    cancelButtonText: "Back",
    confirmButtonColor: "#f09a21",
    cancelButtonColor: "#f1f5f9",
    reverseButtons: true,
    focusConfirm: true,
    customClass: {
      popup: "score-mismatch-input-popup",
    },
    inputValidator: (value) => {
      if (!value || isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
        return "Please enter a valid positive number for total marks.";
      }
    },
  });

  if (newScore) {
    const parsed = parseFloat(newScore);
    if (!isNaN(parsed) && parsed > 0 && onSave) {
      await onSave(parsed);
    }
    return parsed;
  } else if (onCancel) {
    onCancel();
  }
  return null;
};
